import { format } from "date-fns";
import { toast } from "sonner";
import { MapPin, CalendarDays, Clock, Users, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

const STATUS_LABELS = {
  pending_deposit: "Pending Deposit",
  open: "Live",
  joinee_selected: "Joinee Selected",
  awaiting_deposits: "Awaiting Deposits",
  confirmed: "Confirmed",
  completed: "Completed",
  cancelled: "Cancelled",
};

const STATUS_BADGE_CLS = {
  pending_deposit: "bg-transparent border border-[#FFB300]/60 text-[#FFB300]",
  open: "bg-transparent border border-[#D4AF37]/60 text-[#D4AF37]",
};

const REQUEST_LABELS = {
  pending: "Requested",
  selected: "You're Selected",
  not_selected: "Not Selected",
};

const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

const PlanMeta = ({ plan, dt }) => (
  <div className="space-y-2 text-sm text-[#E0E0E0] mb-4">
    <p className="flex items-center gap-2" data-testid="plan-card-neighborhood">
      <MapPin className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
      {plan.neighborhood}
    </p>
    <p className="flex items-center gap-2" data-testid="plan-card-datetime">
      <CalendarDays className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
      {format(dt, "EEE, d MMM yyyy")}
      <Clock className="w-4 h-4 text-[#D4AF37] ml-2" strokeWidth={1.5} />
      {format(dt, "h:mm a")}
    </p>
  </div>
);

const DepositSplit = ({ plan }) => (
  <>
    <div className="flex items-baseline justify-between mb-4">
      <span className="text-xs uppercase tracking-widest text-[#A0A0A0]">Estimated Bill</span>
      <span className="font-serif text-2xl text-[#D4AF37] font-semibold" data-testid="plan-card-bill">
        {inr(plan.estimated_bill)}
      </span>
    </div>
    <div className="rounded-lg border border-[#D4AF37]/15 bg-[#0B0B0F]/60 px-4 py-3 mb-4 flex justify-between text-xs" data-testid="plan-card-deposits">
      <span className="text-[#A0A0A0]">
        Planner deposit <span className="text-white">(40%)</span>
        <span className="block text-[#D4AF37] text-sm font-semibold mt-0.5">{inr(plan.planner_deposit)}</span>
      </span>
      <span className="text-[#A0A0A0] text-right">
        Joinee deposit <span className="text-white">(25%)</span>
        <span className="block text-[#D4AF37] text-sm font-semibold mt-0.5">{inr(plan.joinee_deposit)}</span>
      </span>
    </div>
  </>
);

const PlannerStrip = ({ planner }) => (
  <div className="flex items-center gap-2.5" data-testid="plan-card-planner">
    <Avatar className="w-9 h-9 border border-[#D4AF37]/30">
      <AvatarImage src={planner?.photo} alt={planner?.name} />
      <AvatarFallback className="bg-[#1A1A24] text-[#D4AF37] text-xs">
        {planner?.name?.[0] || "?"}
      </AvatarFallback>
    </Avatar>
    <div>
      <p className="text-sm text-white font-medium leading-tight">{planner?.name}</p>
      <p className="text-[10px] uppercase tracking-widest text-[#A0A0A0]">Planner</p>
    </div>
  </div>
);

const PlanAction = ({ plan, isMine, onJoin, busy }) => {
  if (isMine) {
    return (
      <span className="text-xs text-[#A0A0A0] italic flex items-center gap-1.5" data-testid="plan-card-own-label">
        <Users className="w-3.5 h-3.5" strokeWidth={1.5} />
        Your table{typeof plan.request_count === "number" ? ` · ${plan.request_count} request${plan.request_count === 1 ? "" : "s"}` : ""}
      </span>
    );
  }
  if (plan.my_request_status) {
    return (
      <Badge
        data-testid="plan-card-request-status"
        className={
          plan.my_request_status === "selected"
            ? "bg-[#D4AF37] text-[#0B0B0F] border-transparent"
            : "bg-transparent border border-white/25 text-[#E0E0E0]"
        }
      >
        {REQUEST_LABELS[plan.my_request_status] || plan.my_request_status}
      </Badge>
    );
  }
  if (plan.status === "open" && onJoin) {
    return (
      <Button
        data-testid="plan-join-button"
        size="sm"
        disabled={busy}
        onClick={() => onJoin(plan.plan_id)}
        className="bg-[#D4AF37] text-[#0B0B0F] hover:bg-[#F1D570] font-semibold rounded-md transition-colors"
      >
        {busy ? "Sending..." : "Request to Join"}
      </Button>
    );
  }
  return null;
};

export const PlanCard = ({ plan, currentUserId, onJoin, busy, index = 0, children }) => {
  const isMine = plan.planner_id === currentUserId;
  const dt = new Date(plan.datetime);
  const canShare = isMine && ["open", "joinee_selected", "awaiting_deposits", "confirmed"].includes(plan.status);

  const handleShare = async () => {
    // Portable link: always built from the current origin (custom domains keep working)
    const shareUrl = `${window.location.origin}/plan/${plan.plan_id}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Join me on THE CIRCLE — ${plan.title}`,
          text: `${plan.neighborhood} · ${format(dt, "EEE, d MMM yyyy · h:mm a")}`,
          url: shareUrl,
        });
      } catch (err) {
        console.debug("Share sheet dismissed", err);
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied");
    } catch (err) {
      console.warn("Clipboard write failed", err);
      toast.error("Could not copy link");
    }
  };

  return (
    <article
      data-testid="plan-card"
      className="fade-up bg-[#15151A] rounded-xl border border-[#D4AF37]/20 p-5 shadow-[0_4px_24px_rgba(0,0,0,0.4)] transition-colors hover:border-[#D4AF37]/40"
      style={{ animationDelay: `${index * 70}ms` }}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <h3 className="font-serif text-xl text-white font-semibold leading-snug" data-testid="plan-card-title">
          {plan.title}
        </h3>
        <div className="flex items-center gap-1.5 shrink-0">
          <Badge
            data-testid="plan-card-status"
            className={STATUS_BADGE_CLS[plan.status] || "bg-[#D4AF37] text-[#0B0B0F] border-transparent"}
          >
            {STATUS_LABELS[plan.status] || plan.status}
          </Badge>
          {canShare && (
            <button
              onClick={handleShare}
              data-testid="plan-share-button"
              title="Share this plan"
              className="p-1.5 text-[#D4AF37] hover:text-[#F1D570] transition-colors"
            >
              <Share2 className="w-4 h-4" strokeWidth={1.5} />
            </button>
          )}
        </div>
      </div>

      <PlanMeta plan={plan} dt={dt} />
      <DepositSplit plan={plan} />

      <div className="flex items-center justify-between">
        <PlannerStrip planner={plan.planner} />
        <PlanAction plan={plan} isMine={isMine} onJoin={onJoin} busy={busy} />
      </div>

      {children}
    </article>
  );
};
